import BoldRegularText from '@/components/BoldRegularText';
import BoldText from '@/components/BoldText';
import RegularText from '@/components/RegularText';
import {
  AccountPlan,
  ProductRecommendType,
  RecommendSource,
  ShippingInfo,
  SORT_DEFAULT_COLLECTIONS,
  SORT_DEFAULT_PRODUCTS,
  SORT_OPTIONS_COLLECTIONS,
  SORT_OPTIONS_PRODUCTS,
} from '@/constants/enum';
import { camelToSnakeCase, checkDirtyField, isLockFeature, isUrlValid, renderErrorValue, splitObjects } from '@/helpers';
import MenuItem from '@/pages/TrackingPage/components/EDD/components/ModalTrackingPage/components/MenuItem';
import { convertClientKeyToApiKey } from '@/pages/TrackingPage/components/Language/config';
import UploadAsset from '@/pages/TrackingPage/components/LookAndFeel/components/ChooseTheme/UploadAsset';
import { isNotChangeData } from '@/pages/TrackingPage/config';
import trackingPageSlice, {
  IConfiguration,
  languageSelector,
  LanguageValue,
  trackingPageSelector,
} from '@/redux/features/trackingPage.slice';
import { Autocomplete, Button, Checkbox, Divider, InlineError, RadioButton, Select, Tag, TextField } from '@shopify/polaris';
import { ChevronLeftIcon, EditIcon } from '@shopify/polaris-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ProgressBarContent from './components/ProgressBarContent';
import SelectedResourceList from './components/SelectedResourceList';
import { accountSelector } from '@/redux/features/plan.slice';
import { useGetProductRecommendQuery } from '@/redux/api/api.caller';
import { footerLinkConfig } from './config';

enum LookupEditType {
  ProgressBar = 'progressBar',
  TrackingHistory = 'orderDetail',
  OrderDetail = 'trackingLogs',
  TrackingCompany = 'trackingCompany',
  ProductRecommendation = 'productRecommendation',
  PolicyAndSocial = 'policyAndSocial',
  Image = 'image',
}

interface IAutoCompleteTextFieldState {
  optionsSelected: Array<{
    value: string;
    label: string;
  }>;
  optionsFilter: Array<{
    value: string;
    label: string;
  }>;
  options: Array<{
    value: string;
    label: string;
  }>;
  baseOptions: {
    type: Array<{
      value: string;
      label: string;
    }>;
    tags: Array<{
      value: string;
      label: string;
    }>;
  };
}

const optionsConfig = [
  { label: 'All product', value: ProductRecommendType.AllProduct, isHasValue: false },
  { label: 'Product Type', value: ProductRecommendType.productType, isHasValue: true },
  { label: 'Product Tags', value: ProductRecommendType.Tags, isHasValue: true },
  { label: 'Specific products', value: ProductRecommendType.SpecificProducts, isHasValue: false },
  { label: 'Collections', value: ProductRecommendType.Collections, isHasValue: false },
];

const PageStatus = () => {
  const dispatch = useDispatch();
  const plan = useSelector(accountSelector);
  const isLock = isLockFeature([AccountPlan.Starter]) && !plan.isOldPaidUser;
  const language = useSelector(languageSelector);
  const { trackingHistory, orderDetails, footer } = language.value;
  const {
    value: data,
    fieldsChange,
    oldValue: {
      lookAndFeel: { content: oldContent },
      configuration: { productRecommendation: oldProductRecommendation, footerLink: oldFooterLink },
    },
    validation,
  } = useSelector(trackingPageSelector);
  const { productRecommendation, footerLink } = data.configuration;
  const { content } = data.lookAndFeel;
  const source = productRecommendation.source ?? RecommendSource.Rule;
  const isRulePath = source === RecommendSource.Rule;

  const isPickerType =
    isRulePath &&
    (productRecommendation.type === ProductRecommendType.SpecificProducts ||
      productRecommendation.type === ProductRecommendType.Collections);
  const isValueType = isRulePath && !!optionsConfig.find((item) => item.value === productRecommendation.type)?.isHasValue;
  const pickerField: 'products' | 'collections' =
    productRecommendation.type === ProductRecommendType.Collections ? 'collections' : 'products';
  const pickerResourceType: 'product' | 'collection' =
    productRecommendation.type === ProductRecommendType.Collections ? 'collection' : 'product';
  const pickerSelection = productRecommendation[pickerField];
  const [currentEditItem, setCurrentEditItem] = useState<LookupEditType>();
  const [state, setState] = useState<IAutoCompleteTextFieldState>({
    optionsSelected: [],
    baseOptions: {
      type: [],
      tags: [],
    },
    optionsFilter: [],
    options: [],
  });
  const [isFocusAutoComplete, setIsFocusAutoComplete] = useState<boolean>(false);
  const productionRecommendData = useGetProductRecommendQuery(undefined, {
    skip: !isFocusAutoComplete || isLock,
  });
  const [inputValue, setInputValue] = useState('');
  const updateText = useCallback((inputValue: string) => {
    setInputValue(inputValue);
    const optionsFilter = state.options.filter((option) => option.label.toUpperCase().includes(inputValue.toUpperCase()));
    setState({ ...state, optionsFilter });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (productionRecommendData.data) {
      const { data } = productionRecommendData.data;
      const type = data.types.map((item) => {
        return { value: item, label: item };
      });
      const tags = data.tags.map((item) => {
        return { value: item, label: item };
      });
      setState({
        ...state,
        baseOptions: { type, tags },
        options: productRecommendation.type === ProductRecommendType.Tags ? tags : type,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productionRecommendData.data]);

  useEffect(() => {
    if (productRecommendation.type === ProductRecommendType.Tags) {
      setState({ ...state, options: state.baseOptions.tags });
    }
    if (productRecommendation.type === ProductRecommendType.productType) {
      setState({ ...state, options: state.baseOptions.type });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productRecommendation.type]);

  useEffect(() => {
    const missingPicked = isPickerType && pickerSelection.length === 0;
    const missingValue = isValueType && (productRecommendation.value?.length ?? 0) === 0;
    const isEmpty = productRecommendation.enable && (missingPicked || missingValue);
    const hasKey = validation.includes('productRecommend');
    if (isEmpty && !hasKey) {
      dispatch(trackingPageSlice.actions.handleChangeValidation([...validation, 'productRecommend']));
    }
    if (!isEmpty && hasKey) {
      dispatch(trackingPageSlice.actions.handleChangeValidation(validation.filter((v) => v !== 'productRecommend')));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productRecommendation.type, productRecommendation.enable, pickerSelection.length, productRecommendation.value?.length, isRulePath]);

  const trackingInformationOptions = useMemo(() => {
    return [
      { id: LookupEditType.ProgressBar, value: ShippingInfo.ProgressBar, label: 'Progress bar & Status labels' },
      // { id: LookupEditType.TrackingHistory, value: ShippingInfo.TrackingLogs, label: 'Tracking history' },
      {
        id: LookupEditType.OrderDetail,
        value: ShippingInfo.OrderDetail,
        label: 'Order detail',
      },
      {
        id: LookupEditType.TrackingCompany,
        value: ShippingInfo.TrackingCompany,
        label: 'Tracking information',
      },
      {
        id: LookupEditType.TrackingCompany,
        value: ShippingInfo.Map,
        label: 'Map',
        isNotEditable: true,
      },
    ];
  }, []);

  const appearanceOptions = useMemo(() => {
    return [{ id: LookupEditType.Image, label: 'Images' }];
  }, []);

  const changeKeyOfObject = useCallback((currentObject: Partial<Record<string, never>>, parentKey: string) => {
    const replacedObject = Object.keys(currentObject).map((key) => {
      const newKey =
        parentKey === 'trackingForm' || parentKey === 'footer'
          ? convertClientKeyToApiKey[parentKey][key as keyof (typeof convertClientKeyToApiKey)['trackingForm' | 'footer']]
          : camelToSnakeCase(key);
      return { [newKey]: currentObject[key] };
    });
    return replacedObject.reduce((a, b) => Object.assign({}, a, b));
  }, []);

  const handleLanguageField = useCallback(
    (currentValue: {}, parentKey: string) => {
      const { dirtyField: parentKeyField } = checkDirtyField(
        language.oldValue.value[parentKey as keyof typeof language.value],
        currentValue,
      );
      return !parentKeyField ? undefined : changeKeyOfObject(parentKeyField, parentKey);
    },
    [changeKeyOfObject, language],
  );

  const handleChangeLanguage = useCallback(
    (parentKey: string, key: string) => (value: string | boolean) => {
      const { fieldsChange } = language;
      dispatch(
        trackingPageSlice.actions.handleLanguageValue({
          ...language.value,
          [parentKey]: {
            ...language.value[parentKey as keyof LanguageValue],
            [key]: value,
          },
        }),
      );
      dispatch(
        trackingPageSlice.actions.handleLanguageFieldsChange({
          ...fieldsChange,
          [parentKey]: handleLanguageField(
            {
              ...language.value[parentKey as keyof LanguageValue],
              [key]: value,
            },
            parentKey,
          ),
        }),
      );
    },
    [dispatch, handleLanguageField, language],
  );

  const handleProductRecommendationField = useCallback(
    (currentValue: {}) => {
      const { dirtyField: enableField } = checkDirtyField(
        { enable: oldProductRecommendation.enable },
        {
          ...currentValue,
        },
      );

      const { dirtyField: titleField } = checkDirtyField(
        { title: oldProductRecommendation.title },
        {
          ...currentValue,
        },
      );

      const { dirtyField: typeField } = checkDirtyField(
        { type: oldProductRecommendation.type },
        {
          ...currentValue,
        },
      );

      const { dirtyField: sortByField } = checkDirtyField({ sortBy: oldProductRecommendation.sortBy }, { ...currentValue });

      const { dirtyField: sourceField } = checkDirtyField({ source: oldProductRecommendation.source }, { ...currentValue });

      if (!enableField && !titleField && !typeField && !sortByField && !sourceField) {
        return undefined;
      }
      return {
        isActive: enableField?.enable,
        title: titleField?.title,
        type: typeField?.type,
        sortBy: sortByField?.sortBy,
        source: sourceField?.source,
      };
    },
    [
      oldProductRecommendation.enable,
      oldProductRecommendation.title,
      oldProductRecommendation.type,
      oldProductRecommendation.sortBy,
      oldProductRecommendation.source,
    ],
  );

  const handleChangeProductRecommendation = useCallback(
    (key: keyof IConfiguration['productRecommendation']) => (value: string | boolean) => {
      dispatch(
        trackingPageSlice.actions.handleTrackingPageValue({
          ...data,
          configuration: {
            ...data.configuration,
            productRecommendation: {
              ...productRecommendation,
              value: !handleProductRecommendationField({ ...productRecommendation, [key]: value })
                ? oldProductRecommendation.value
                : [],
              [key]: value,
            },
          },
        }),
      );
      dispatch(
        trackingPageSlice.actions.handleTrackingPageFieldsChange({
          ...fieldsChange,
          productRecommend: handleProductRecommendationField({
            ...productRecommendation,
            [key]: value,
          }),
        }),
      );
    },
    [data, dispatch, fieldsChange, handleProductRecommendationField, oldProductRecommendation.value, productRecommendation],
  );

  const handleChangeType = useCallback(
    (value: string) => {
      const isCollections = value === ProductRecommendType.Collections;
      const validKeys = isCollections ? ['CREATED', 'BEST_SELLING', 'COLLECTION_DEFAULT'] : ['CREATED_AT'];
      const currentKey = productRecommendation.sortBy.split(':')[0];
      const nextSortBy = validKeys.includes(currentKey)
        ? productRecommendation.sortBy
        : isCollections
        ? SORT_DEFAULT_COLLECTIONS
        : SORT_DEFAULT_PRODUCTS;
      const nextProductRecommendation = { ...productRecommendation, type: value, sortBy: nextSortBy };
      dispatch(
        trackingPageSlice.actions.handleTrackingPageValue({
          ...data,
          configuration: {
            ...data.configuration,
            productRecommendation: {
              ...nextProductRecommendation,
              value: !handleProductRecommendationField(nextProductRecommendation) ? oldProductRecommendation.value : [],
            },
          },
        }),
      );
      dispatch(
        trackingPageSlice.actions.handleTrackingPageFieldsChange({
          ...fieldsChange,
          productRecommend: handleProductRecommendationField(nextProductRecommendation),
        }),
      );
    },
    [data, dispatch, fieldsChange, handleProductRecommendationField, oldProductRecommendation.value, productRecommendation],
  );

  const handleChangeSortBy = useCallback(
    (value: string) => {
      const next = { ...productRecommendation, sortBy: value };
      dispatch(
        trackingPageSlice.actions.handleTrackingPageValue({
          ...data,
          configuration: { ...data.configuration, productRecommendation: next },
        }),
      );
      dispatch(
        trackingPageSlice.actions.handleTrackingPageFieldsChange({
          ...fieldsChange,
          productRecommend: handleProductRecommendationField(next),
        }),
      );
    },
    [data, dispatch, fieldsChange, handleProductRecommendationField, productRecommendation],
  );

  const handleChangeSource = useCallback(
    (value: string) => {
      dispatch(
        trackingPageSlice.actions.handleTrackingPageValue({
          ...data,
          configuration: {
            ...data.configuration,
            productRecommendation: { ...productRecommendation, source: value },
          },
        }),
      );
      const nextProductRecommend = { ...fieldsChange.productRecommend };
      if (value === (oldProductRecommendation.source ?? RecommendSource.Rule)) {
        delete nextProductRecommend.source;
      } else {
        nextProductRecommend.source = value;
      }
      dispatch(
        trackingPageSlice.actions.handleTrackingPageFieldsChange({
          ...fieldsChange,
          productRecommend: Object.keys(nextProductRecommend).length ? nextProductRecommend : undefined,
        }),
      );
    },
    [data, dispatch, fieldsChange, productRecommendation, oldProductRecommendation.source],
  );

  const writePickerSelection = useCallback(
    (picked: Array<{ id: string; handle: string; title?: string; image?: string }>) => {
      dispatch(
        trackingPageSlice.actions.handleTrackingPageValue({
          ...data,
          configuration: {
            ...data.configuration,
            productRecommendation: { ...productRecommendation, [pickerField]: picked },
          },
        }),
      );
      dispatch(
        trackingPageSlice.actions.handleTrackingPageFieldsChange({
          ...fieldsChange,
          productRecommend: { ...fieldsChange.productRecommend, [pickerField]: picked },
        }),
      );
    },
    [data, dispatch, fieldsChange, pickerField, productRecommendation],
  );

  const handleOpenPicker = useCallback(async () => {
    const shopify = (window as any).shopify;
    if (!shopify?.resourcePicker) return;
    const selection = await shopify.resourcePicker({
      type: pickerResourceType,
      multiple: true,
      // Whole products only (no variant drill-down); hide products not published to any channel.
      filter: { variants: false, hidden: false },
      selectionIds: pickerSelection.map((r) => ({ id: r.id })),
    });
    if (!selection) return; // cancelled
    const pickImage = (r: any): string =>
      r?.images?.[0]?.originalSrc ?? r?.images?.[0]?.url ?? r?.image?.originalSrc ?? r?.image?.url ?? '';
    writePickerSelection(
      selection.map((r: any) => ({ id: r.id, handle: r.handle, title: r.title ?? '', image: pickImage(r) })),
    );
  }, [pickerResourceType, pickerSelection, writePickerSelection]);

  const handleRemovePickerItem = useCallback(
    (id: string) => () => {
      writePickerSelection(pickerSelection.filter((r) => r.id !== id));
    },
    [pickerSelection, writePickerSelection],
  );

  const handleChangeFooterLink = useCallback(
    (key: keyof typeof footerLink) => (value: (typeof footerLink)[keyof typeof footerLink]) => {
      dispatch(trackingPageSlice.actions.handleChangeValidation(validation.filter((item) => item !== key)));
      dispatch(
        trackingPageSlice.actions.handleTrackingPageValue({
          ...data,
          configuration: {
            ...data.configuration,
            footerLink: {
              ...footerLink,
              [key]: value,
            },
          },
        }),
      );
      const { dirtyField: policyField } = checkDirtyField(splitObjects(oldFooterLink, 0, 4), {
        ...splitObjects(footerLink, 0, 4),
        [key]: value,
      });
      const { dirtyField: socialField } = checkDirtyField(
        splitObjects(oldFooterLink, 4, Object.entries(oldFooterLink).length - 1),
        {
          ...splitObjects(footerLink, 4, Object.entries(footerLink).length - 1),
          [key]: value,
        },
      );
      const { dirtyField: isEnabledField } = checkDirtyField(
        { isEnabledFooterLink: oldFooterLink.isEnabledFooterLink },
        {
          ...footerLink,
          [key]: value,
        },
      );
      dispatch(
        trackingPageSlice.actions.handleTrackingPageFieldsChange({
          ...fieldsChange,
          footerLinks:
            !policyField && !socialField && !isEnabledField
              ? undefined
              : {
                  social: socialField,
                  policy: policyField,
                  ...isEnabledField,
                },
        }),
      );
    },
    [data, dispatch, fieldsChange, footerLink, oldFooterLink, validation],
  );

  const additionalOptions = useMemo(() => {
    return [
      {
        id: LookupEditType.ProductRecommendation,
        label: 'Product recommendation',
        isChecked: productRecommendation.enable,
        onChange: handleChangeProductRecommendation('enable'),
      },
      {
        id: LookupEditType.PolicyAndSocial,
        label: 'Policy & Social links',
        isChecked: footerLink.isEnabledFooterLink,
        onChange: handleChangeFooterLink('isEnabledFooterLink'),
      },
    ];
  }, [footerLink.isEnabledFooterLink, handleChangeFooterLink, handleChangeProductRecommendation, productRecommendation.enable]);

  const handleShippingInfo = (value: ShippingInfo) => () => {
    const shippingInfo = [...data.lookAndFeel.content.shippingInfo];
    const index = shippingInfo.findIndex((item) => item === value);
    if (index === -1) {
      shippingInfo.push(value);
    } else {
      shippingInfo.splice(index, 1);
    }
    dispatch(
      trackingPageSlice.actions.handleTrackingPageValue({
        ...data,
        lookAndFeel: {
          ...data.lookAndFeel,
          content: {
            ...content,
            shippingInfo,
          },
        },
      }),
    );
    dispatch(
      trackingPageSlice.actions.handleTrackingPageFieldsChange({
        ...fieldsChange,
        general:
          isNotChangeData(oldContent.shippingInfo, shippingInfo) && Object.entries(fieldsChange.general).length < 2
            ? undefined
            : {
                ...fieldsChange.general,
                show: shippingInfo.join(','),
              },
      }),
    );
  };

  const handleChangeSelected = useCallback(
    (value: Array<string>) => {
      dispatch(
        trackingPageSlice.actions.handleTrackingPageValue({
          ...data,
          configuration: {
            ...data.configuration,
            productRecommendation: {
              ...productRecommendation,
              value,
            },
          },
        }),
      );
      dispatch(
        trackingPageSlice.actions.handleTrackingPageFieldsChange({
          ...fieldsChange,
          productRecommend:
            isNotChangeData(oldProductRecommendation.value, value) && Object.entries(fieldsChange.productRecommend).length < 3
              ? undefined
              : {
                  ...fieldsChange.productRecommend,
                  tags: productRecommendation.type === ProductRecommendType.Tags ? value : undefined,
                  productType: productRecommendation.type === ProductRecommendType.productType ? value : undefined,
                },
        }),
      );
    },
    [data, dispatch, fieldsChange, oldProductRecommendation.value, productRecommendation],
  );

  const handleRemoveTag = useCallback(
    (value: string) => () => {
      const selected = [...data.configuration.productRecommendation.value];
      const index = selected.findIndex((item) => item === value);
      selected.splice(index, 1);
      dispatch(
        trackingPageSlice.actions.handleTrackingPageValue({
          ...data,
          configuration: {
            ...data.configuration,
            productRecommendation: {
              ...productRecommendation,
              value: selected,
            },
          },
        }),
      );

      dispatch(
        trackingPageSlice.actions.handleTrackingPageFieldsChange({
          ...fieldsChange,
          productRecommend:
            isNotChangeData(oldProductRecommendation.value, selected) &&
            Object.entries(fieldsChange.productRecommend || {}).length < 3
              ? undefined
              : {
                  ...fieldsChange.productRecommend,
                  tags: productRecommendation.type === ProductRecommendType.Tags ? selected : undefined,
                  productType: productRecommendation.type === ProductRecommendType.productType ? selected : undefined,
                },
        }),
      );
    },
    [data, dispatch, fieldsChange, oldProductRecommendation.value, productRecommendation],
  );

  const handleBlurTextField = useCallback(
    (value: string | boolean | undefined, key: string) => {
      if (typeof value === 'boolean') return;
      if (value && !isUrlValid(value)) {
        const isNotHaveKey = validation.findIndex((item) => item === key) === -1;
        if (isNotHaveKey) {
          dispatch(trackingPageSlice.actions.handleChangeValidation([...validation, key]));
        }
      } else {
        dispatch(trackingPageSlice.actions.handleChangeValidation(validation.filter((item) => item !== key)));
      }
    },
    [dispatch, validation],
  );

  const renderEditContent = useCallback(() => {
    switch (currentEditItem) {
      case LookupEditType.ProgressBar:
        return <ProgressBarContent />;
      case LookupEditType.TrackingHistory:
        return (
          <div className="px-16">
            <TextField
              maxLength={100}
              showCharacterCount
              label="Title"
              placeholder="Shipment history"
              autoComplete="off"
              value={trackingHistory.shipmentHistory}
              onChange={handleChangeLanguage('trackingHistory', 'shipmentHistory')}
              disabled={isLock}
            />
          </div>
        );
      case LookupEditType.OrderDetail:
        return (
          <div className="px-16 d-flex flex-column gap-8">
            <TextField
              maxLength={100}
              showCharacterCount
              label="Title"
              placeholder="Shipment items"
              autoComplete="off"
              value={orderDetails.shipmentItems}
              onChange={handleChangeLanguage('orderDetails', 'shipmentItems')}
              disabled={isLock}
            />
            {/* <TextField
              maxLength={100}
              showCharacterCount
              label="Product"
              placeholder="Products"
              autoComplete="off"
              value={orderDetails.product}
              onChange={handleChangeLanguage('orderDetails', 'product')}
              disabled={isLock}
            /> */}
            <TextField
              maxLength={100}
              showCharacterCount
              label="Total price"
              placeholder="Total"
              autoComplete="off"
              value={orderDetails.totalPrice}
              onChange={handleChangeLanguage('orderDetails', 'totalPrice')}
              disabled={isLock}
            />
            {/* <TextField
              maxLength={100}
              showCharacterCount
              label="Item singular"
              placeholder="Item"
              autoComplete="off"
              value={orderDetails.itemSingular}
              onChange={handleChangeLanguage('orderDetails', 'itemSingular')}
              disabled={isLock}
            />
            <TextField
              maxLength={100}
              showCharacterCount
              label="Item plural"
              placeholder="Items"
              autoComplete="off"
              value={orderDetails.itemPlural}
              onChange={handleChangeLanguage('orderDetails', 'itemPlural')}
              disabled={isLock}
            /> */}
          </div>
        );
      case LookupEditType.TrackingCompany:
        return (
          <div className="px-16 d-flex flex-column gap-8">
            <TextField
              maxLength={100}
              showCharacterCount
              label="Title"
              placeholder="Shipment information"
              autoComplete="off"
              value={trackingHistory.shipmentInformation}
              onChange={handleChangeLanguage('trackingHistory', 'shipmentInformation')}
              disabled={isLock}
            />
            <TextField
              maxLength={100}
              showCharacterCount
              label="Shipping address"
              placeholder="Shipping address"
              autoComplete="off"
              value={trackingHistory.shippingAddress}
              onChange={handleChangeLanguage('trackingHistory', 'shippingAddress')}
              disabled={isLock}
            />
            {/* <TextField
              maxLength={100}
              showCharacterCount
              label="Tracking number"
              placeholder="Tracking number"
              autoComplete="off"
              value={trackingHistory.trackingNumber}
              onChange={handleChangeLanguage('trackingHistory', 'trackingNumber')}
              disabled={isLock}
            />
            <TextField
              maxLength={100}
              showCharacterCount
              label="Carrier"
              placeholder="Carrier"
              autoComplete="off"
              value={trackingHistory.carrier}
              onChange={handleChangeLanguage('trackingHistory', 'carrier')}
              disabled={isLock}
            /> */}
          </div>
        );
      case LookupEditType.Image:
        return (
          <div className="px-16">
            <UploadAsset />
          </div>
        );
      case LookupEditType.ProductRecommendation:
        return (
          <div className="px-16 d-flex flex-column gap-8">
            <TextField
              label="Title"
              placeholder="Recommend Products"
              autoComplete="off"
              value={language.value.productRecommend.title}
              onChange={handleChangeLanguage('productRecommend', 'title')}
              maxLength={100}
              showCharacterCount
              disabled={isLock}
            />
            <div className="d-flex flex-column gap-8">
              <RadioButton
                label="Rule based"
                checked={source === RecommendSource.Rule}
                id="rec-source-rule"
                name="rec-source"
                onChange={() => handleChangeSource(RecommendSource.Rule)}
                disabled={isLock}
              />
              <RadioButton
                label="Shopify related products"
                checked={source === RecommendSource.Shopify}
                id="rec-source-shopify"
                name="rec-source"
                onChange={() => handleChangeSource(RecommendSource.Shopify)}
                disabled={isLock}
              />
            </div>
            {isRulePath && (
              <Select
                label="Type"
                options={optionsConfig.map((item) => ({
                  label: item.label,
                  value: item.value,
                }))}
                onChange={handleChangeType}
                value={productRecommendation.type}
                disabled={isLock}
              />
            )}
            {isRulePath && (
              <Select
                label="Sort by"
                options={
                  productRecommendation.type === ProductRecommendType.Collections
                    ? SORT_OPTIONS_COLLECTIONS
                    : SORT_OPTIONS_PRODUCTS
                }
                onChange={handleChangeSortBy}
                value={productRecommendation.sortBy}
                disabled={isLock}
              />
            )}
            {isRulePath && optionsConfig.find((item) => item.value === productRecommendation.type)?.isHasValue ? (
              <div>
                <Autocomplete
                  options={state.options}
                  selected={productRecommendation.value}
                  onSelect={handleChangeSelected}
                  textField={
                    <div onFocus={() => setIsFocusAutoComplete(true)}>
                      <Autocomplete.TextField
                        onChange={updateText}
                        label="Select your type/tag for recommend products"
                        value={inputValue}
                        autoComplete="off"
                        disabled={isLock}
                      />
                    </div>
                  }
                  allowMultiple
                  loading={productionRecommendData.isLoading}
                />
                <div style={{ marginTop: 4 }}>
                  <InlineError
                    message={renderErrorValue(validation, 'productRecommend', 'Select at least one item')}
                    fieldID="product-recommend-value"
                  />
                </div>
                <div className="mt-8 d-flex flex-wrap gap-8">
                  {productRecommendation.value.length > 0 &&
                    productRecommendation.value.map((option) => {
                      return (
                        <div key={option}>
                          <Tag onRemove={handleRemoveTag(option)} disabled={isLock}>
                            {option}
                          </Tag>
                        </div>
                      );
                    })}
                </div>
              </div>
            ) : null}
            {isPickerType ? (
              <div>
                <Button onClick={handleOpenPicker} disabled={isLock}>
                  {pickerResourceType === 'collection' ? 'Select collections' : 'Select products'}
                </Button>
                <div style={{ marginTop: 4 }}>
                  <InlineError
                    message={renderErrorValue(validation, 'productRecommend', 'Select at least one item')}
                    fieldID="product-recommend-picker"
                  />
                </div>
                <SelectedResourceList
                  items={pickerSelection}
                  onRemove={(id) => handleRemovePickerItem(id)()}
                  resourceType={pickerResourceType}
                  disabled={isLock}
                />
              </div>
            ) : null}
          </div>
        );
      case LookupEditType.PolicyAndSocial:
        const renderTextFields = (
          fields: {
            key: string;
            title: string;
            placeHolder: string;
            labelKey?: string;
            labelPlaceHolder?: string;
          }[],
        ) => {
          return fields.map((item) => {
            const hasLabel = item.labelKey && item.labelPlaceHolder;
            return (
              <>
                {hasLabel ? (
                  <>
                    <BoldRegularText fontWeight="medium">{item.title}</BoldRegularText>
                    <TextField
                      placeholder={item.labelPlaceHolder}
                      id={item.labelKey}
                      key={item.labelKey}
                      label={'Label'}
                      onChange={handleChangeLanguage('footer', item.labelKey as keyof typeof footer)}
                      value={footer[item.labelKey as keyof typeof footer] + ''}
                      autoComplete="off"
                      maxLength={100}
                      showCharacterCount
                      disabled={isLock}
                    />
                  </>
                ) : null}
                <TextField
                  placeholder={item.placeHolder}
                  id={item.key}
                  key={item.key}
                  label={hasLabel ? 'Link' : item.title}
                  onChange={handleChangeFooterLink(item.key as keyof typeof footerLink)}
                  value={footerLink[item.key as keyof typeof footerLink] + ''}
                  onBlur={() => handleBlurTextField(footerLink[item.key as keyof typeof footerLink], item.key)}
                  autoComplete="off"
                  maxLength={100}
                  showCharacterCount
                  error={renderErrorValue(validation, item.key)}
                  disabled={isLock}
                />
              </>
            );
          });
        };
        return (
          <>
            <div className="px-16 d-flex flex-column gap-8 pb-8">
              <BoldRegularText>Policy</BoldRegularText>
              {renderTextFields(footerLinkConfig.policy)}
            </div>
            <Divider />
            <div className="px-16 d-flex flex-column gap-8">
              <BoldRegularText>Social media</BoldRegularText>
              {renderTextFields(footerLinkConfig.social)}
            </div>
          </>
        );

      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentEditItem,
    footer,
    footerLink,
    handleBlurTextField,
    handleChangeFooterLink,
    handleChangeLanguage,
    handleChangeProductRecommendation,
    handleChangeSelected,
    handleChangeSortBy,
    handleChangeSource,
    handleChangeType,
    handleOpenPicker,
    handleRemovePickerItem,
    handleRemoveTag,
    inputValue,
    isLock,
    isPickerType,
    isRulePath,
    pickerResourceType,
    pickerSelection,
    source,
    orderDetails.itemPlural,
    orderDetails.itemSingular,
    orderDetails.product,
    orderDetails.shipmentItems,
    orderDetails.totalPrice,
    productRecommendation.sortBy,
    productRecommendation.title,
    productRecommendation.type,
    productRecommendation.value,
    productionRecommendData.isLoading,
    state.options,
    trackingHistory.carrier,
    trackingHistory.shipmentHistory,
    trackingHistory.shipmentInformation,
    trackingHistory.shippingAddress,
    trackingHistory.trackingNumber,
    updateText,
    validation,
  ]);

  return currentEditItem ? (
    <>
      <MenuItem>
        <div className="d-flex align-center gap-4">
          <Button
            icon={ChevronLeftIcon}
            onClick={() => setCurrentEditItem(undefined)}
            variant="monochromePlain"
            disabled={isLock}
          />
          <BoldText>
            {
              [...trackingInformationOptions, ...appearanceOptions, ...additionalOptions].find(
                (item) => item.id === currentEditItem,
              )?.label
            }
          </BoldText>
        </div>
      </MenuItem>
      <div className="py-16 border-bottom d-flex flex-column gap-8 edit-content-left">{renderEditContent()}</div>
    </>
  ) : (
    <>
      <MenuItem isHeader>
        <BoldText>Order status page</BoldText>
      </MenuItem>

      <MenuItem tone="tertiary">
        <BoldText customFontSize>Tracking information</BoldText>
      </MenuItem>
      {trackingInformationOptions.map((item) => (
        <MenuItem
          actionIcon={!item.isNotEditable ? EditIcon : undefined}
          onAction={!item.isNotEditable ? () => setCurrentEditItem(item.id) : undefined}
          key={item.id}
          disabled={isLock}
        >
          <Checkbox
            label={<RegularText>{item.label}</RegularText>}
            checked={content.shippingInfo.includes(item.value)}
            onChange={handleShippingInfo(item.value)}
            disabled={isLock}
          />
        </MenuItem>
      ))}

      {/* {chooseTheme === '2' ? (
        <>
          <MenuItem tone="tertiary">
            <BoldText customFontSize>Appearance</BoldText>
          </MenuItem>
          {appearanceOptions.map((item) => (
            <MenuItem actionIcon={EditIcon} onAction={() => setCurrentEditItem(item.id)} key={item.id} disabled={isLock}>
              <RegularText>{item.label}</RegularText>
            </MenuItem>
          ))}
        </>
      ) : null} */}

      <MenuItem tone="tertiary">
        <BoldText customFontSize>Additional sections</BoldText>
      </MenuItem>
      {additionalOptions.map((item) => (
        <MenuItem actionIcon={EditIcon} onAction={() => setCurrentEditItem(item.id)} key={item.id} disabled={isLock}>
          <Checkbox
            checked={item.isChecked}
            onChange={item.onChange}
            label={<RegularText>{item.label}</RegularText>}
            disabled={isLock}
          />
        </MenuItem>
      ))}
    </>
  );
};

export default PageStatus;
